import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        // Invalid/expired refresh token — clear stale session
        if (error?.message?.toLowerCase()?.includes('refresh token') || error?.status === 400) {
          supabase?.auth?.signOut();
        }
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session?.user?.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      // Handle token refresh failure — clear stale auth state
      if (_event === 'TOKEN_REFRESHED' && !session) {
        supabase?.auth?.signOut();
        setUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }
      if (_event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
        return;
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session?.user?.id);
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId) => {
    try {
      // Check if supabase client is initialized
      if (!supabase) {
        console.error('Supabase client not initialized');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

      if (error) throw error;
      
      // If no profile exists yet, set to null and return
      if (!data) {
        setUserProfile(null);
        setLoading(false);
        return;
      }
      
      // Convert snake_case to camelCase
      const profile = {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        avatarUrl: data?.avatar_url,
        role: data?.role,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
      
      setUserProfile(profile);
    } catch (error) {
      // Log full error object for network/fetch errors
      console.error('Error fetching user profile:', error);
      // Set userProfile to null on error to prevent stale data
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email, password, fullName) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'user'
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error?.message);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      setUser(null);
      setUserProfile(null);
      return { error: null };
    } catch (error) {
      console.error('Sign out error:', error?.message);
      return { error };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/reset-password`
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Reset password error:', error?.message);
      return { data: null, error };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password: newPassword
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update password error:', error?.message);
      return { data: null, error };
    }
  };

  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      // Convert camelCase to snake_case for database
      const snakeUpdates = {};
      if (updates?.fullName !== undefined) snakeUpdates.full_name = updates?.fullName;
      if (updates?.avatarUrl !== undefined) snakeUpdates.avatar_url = updates?.avatarUrl;

      const { data, error } = await supabase?.from('user_profiles')?.update(snakeUpdates)?.eq('id', user?.id)?.select()?.single();

      if (error) throw error;

      // Update local state with camelCase
      const updatedProfile = {
        id: data?.id,
        email: data?.email,
        fullName: data?.full_name,
        avatarUrl: data?.avatar_url,
        role: data?.role,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
      
      setUserProfile(updatedProfile);
      return { data: updatedProfile, error: null };
    } catch (error) {
      console.error('Update profile error:', error?.message);
      return { data: null, error };
    }
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  const isAdmin = () => {
    return userProfile?.role === 'admin';
  };

  const value = {
    user,
    userProfile,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile,
    isEmailVerified,
    isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
